// v3 value story snapshot schemas and types, split out of value-story.ts so the deck and the v2→v3
// migration can parse a saved story without importing the engine (the calculator→deck boundary:
// math-free modules only). value-story.ts re-exports everything here, so existing imports keep working.
//
// `v: 2` — v1 is the v2-calculator snapshot (types.ts StorySnapshot). A rollback that only knows v1
// can refuse a v2 story instead of misreading it (Q58). Zod schemas because this JSON crosses the
// SFDC boundary.

import { z } from 'zod';

export const VALUE_STORY_VERSION = 2 as const;

const RecordLevelSchema = z.enum(['accounts', 'contacts']);
const StepUnitSchema = z.enum(['count', 'pct', 'money', 'hours']);

export const StoryFormulaStepSchema = z.object({
  label: z.string(),
  description: z.string(),
  operator: z.enum(['×', '−', '=']).nullable(),
  value: z.number(),
  unit: StepUnitSchema,
  subTotal: z.string().nullable(),
});

export const StorySubTotalSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
  unit: StepUnitSchema,
});

export const StorySectionSchema = z.object({
  level: RecordLevelSchema.nullable(),
  steps: z.array(StoryFormulaStepSchema),
  subTotals: z.array(StorySubTotalSchema),
  value: z.number(),
  columns: z
    .object({ today: z.array(StoryFormulaStepSchema), withClay: z.array(StoryFormulaStepSchema) })
    .optional(),
});

export const StoryMetricSchema = z.object({
  key: z.string(),
  name: z.string(),
  unit: z.string(),
  measurement: z.enum(['telemetry-capable', 'customer-reported']),
  recordLevel: RecordLevelSchema.nullable(),
  today: z.number(),
  withClay: z.number().nullable(),
  assumed: z.boolean(),
});

export const StoryInputSchema = z.object({
  key: z.string(),
  label: z.string(),
  role: z.enum(['scaling', 'current', 'with-clay']),
  kind: z.enum(['count', 'pct', 'hrs', 'min', 'money', 'rate']),
  recordLevel: RecordLevelSchema.nullable(),
  value: z.number(),
  /** true when the value came from the question default rather than the customer. */
  assumed: z.boolean(),
});

/** A customer proof point carried into the snapshot so the deck never reads the benchmark table. */
export const StoryProofSchema = z.object({
  customer: z.string(),
  pre: z.string(),
  post: z.string(),
  imp: z.string(),
  notes: z.string(),
});

export const StoryUseCaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.enum(['Crawl', 'Walk', 'Run', 'Efficiency']),
  gridRow: z.enum(['Data foundation', 'Grow pipeline', 'Win deals', 'Efficiency']),
  confidence: z.enum(['Benchmarked', 'Partial']),
  outcome: z.enum(['revenue', 'cost']),
  path: z.enum(['pipeline', 'win-rate', 'cost']),
  benchmarkClaim: z.string(),
  recordLevels: z.array(RecordLevelSchema),
  /** Calculation methods that were on (GTM automation); absent on older snapshots = the first only. */
  methods: z.array(z.string()).optional(),
  /** Gross value, before realization. */
  value: z.number(),
  realizationPct: z.number(),
  programValue: z.number(),
  attributionPct: z.number(),
  attributedValue: z.number(),
  /** Gross pipeline created; 0 outside the pipeline path. */
  pipeline: z.number(),
  sections: z.array(StorySectionSchema),
  metrics: z.array(StoryMetricSchema),
  inputs: z.array(StoryInputSchema),
  notes: z.array(z.string()),
  displacedTools: z
    .array(z.object({ id: z.string().nullable(), name: z.string(), spend: z.number() }))
    .nullable(),
  stories: z.array(StoryProofSchema),
});

export const ValueStorySchema = z.object({
  v: z.literal(VALUE_STORY_VERSION),
  created: z.number(),
  customerName: z.string(),
  deal: z.object({
    acv: z.number(),
    winRate: z.number(),
    contractEstimate: z.number().nullable(),
    currentAnnualPipeline: z.number().nullable(),
  }),
  rollup: z.object({
    gross: z.number(),
    program: z.number(),
    attributed: z.number(),
    pipelineGross: z.number(),
    pipeline: z.number(),
    roi: z.number().nullable(),
  }),
  useCases: z.array(StoryUseCaseSchema),
});

export type StoryFormulaStep = z.infer<typeof StoryFormulaStepSchema>;
export type StoryMetric = z.infer<typeof StoryMetricSchema>;
export type StoryInput = z.infer<typeof StoryInputSchema>;
export type StoryProof = z.infer<typeof StoryProofSchema>;
export type StoryUseCase = z.infer<typeof StoryUseCaseSchema>;
export type ValueStory = z.infer<typeof ValueStorySchema>;
